import { DossierNode, DossierSettlement, DossierSettlementEvent, DossierSettlementEventKind } from '../../api/dossier';
import { Timeline, TimelineEvent } from '../data/cockpit';
import { money, when } from '../data/format';
import { DOC_COLORS, FlowDocument, FlowItem, FlowLook } from '../components/FlowTimeline';

// What happens to the money of a payment rather than to the invoice: those go on the payment's own line.
const PAYMENT_KINDS: DossierSettlementEventKind[] = ['Payment', 'BankPayment', 'BankRefund', 'Chargeback', 'BankRemoved'];
const PAYMENT_ROLES = ['Payment', 'Chargeback', 'Refund'];

const entryOf = (reference?: string | null) => /boekstuk (\d+)/.exec(reference ?? '')?.[1];

/** What happened, in the words of the screen: the kind of event and the Exact entry it made. */
const titleOf = (e: DossierSettlementEvent) => {
    const entry = entryOf(e.reference);
    const withEntry = (title: string) => (entry ? `${title} · boekstuk ${entry}` : title);
    switch (e.kind) {
        case 'Invoice':
            return 'Factuur aangemaakt';
        case 'SalesEntry':
            return withEntry('Verkoopboeking');
        case 'BankPayment':
            return withEntry('Bankboeking');
        case 'BankRefund':
            return withEntry('Terugbetaling via de bank');
        case 'BankRemoved':
            return withEntry('Bankboeking verwijderd');
        case 'Payment':
            return e.byConnector ? 'Betaling vastgelegd' : e.title === 'Betaling' ? 'Betaling ontvangen' : e.title;
        case 'CreditNote':
            return 'Creditnota aangemaakt';
        case 'CreditNoteBooked':
            return withEntry('Creditnota geboekt');
        case 'Matched':
            return 'Afgeletterd';
        case 'PartlyMatched':
            return 'Deels afgeletterd';
        case 'Chargeback':
            return 'Gestorneerd';
        default:
            return e.title;
    }
};

/** The rest of the reference after the entry number, such as the journal; not the id the document is named after. */
const restOf = (reference: string | null | undefined, label: string) =>
    (reference ?? '')
        .split(' · ')
        .filter((part) => part && !/^boekstuk \d+$/.test(part) && !/^grootboek /.test(part) && !label.includes(part))
        .join(' · ');

export interface SettlementExtra {
    events: TimelineEvent[];
    documents: { id: string; label: string }[];
}

/**
 * What Chargebee and Exact themselves say happened, as timeline events in their own lanes, each on the line of the
 * document it is about: the invoice's own moments on the invoice, a payment's (in Chargebee and its bank entry in
 * Exact) on that payment.
 *
 * The connector logs its failures, hardly its successes: when Chargebee issued an invoice, took a payment or Exact
 * matched it is nowhere in its tables. A dossier that asked both systems has those moments per invoice.
 */
export const settlementEvents = (settlements: DossierSettlement[] | undefined, timeline?: Timeline, nodes: DossierNode[] = []): SettlementExtra => {
    const known = new Map((timeline?.documents ?? []).map((d) => [d.id, d.label]));
    const added = new Map<string, string>();
    const labelOf = (id: string) => known.get(id) ?? added.get(id) ?? id;
    const ensure = (id: string, label: string) => {
        if (!known.has(id) && !added.has(id)) added.set(id, label);
        return id;
    };
    const payments = nodes.filter((n) => PAYMENT_ROLES.includes(n.role));
    const paymentLabel = (n: DossierNode) => `${n.role === 'Chargeback' ? 'Stornering' : n.role === 'Refund' ? 'Terugbetaling' : 'Betaling'} ${n.relationId}`;

    const documentOf = (s: DossierSettlement, e: DossierSettlementEvent) => {
        if (!e.kind || !PAYMENT_KINDS.includes(e.kind)) return ensure(s.invoiceKey, `Factuur ${s.invoiceId}`);
        const entry = entryOf(e.reference);
        const payment =
            payments.find((n) => n.relationId === e.reference || (!!entry && n.entryNumber === entry)) ??
            // A dossier around one payment: what happened to money is about that payment.
            (payments.length === 1 ? payments[0] : undefined);
        if (payment) return ensure(payment.key, paymentLabel(payment));
        return ensure(`settle|${entry ?? e.reference ?? s.invoiceKey}`, entry ? `Bankboeking ${entry}` : `Betaling ${e.reference ?? s.invoiceId}`);
    };

    const events = (settlements ?? []).flatMap((s) =>
        s.events.map((e): TimelineEvent => {
            const documentId = documentOf(s, e);
            const title = titleOf(e);
            return {
                at: e.sortAt ?? e.at ?? null,
                documentId,
                lane: e.side === 'Chargebee' ? 'cb' : 'ex',
                app: null,
                state: e.warning ? 'bad' : e.unmatched ? 'wait' : 'ok',
                title: e.undone ? `${title} · teruggedraaid` : title,
                detail:
                    [e.amount !== null && e.amount !== undefined ? money(e.amount) : null, restOf(e.reference, labelOf(documentId)), e.note]
                        .filter(Boolean)
                        .join(' · ') || null,
                direct: !e.byConnector,
                // Exact gives a day; its sort key is a time only when the connector's own moment gave one.
                dayOnly: !!e.dayOnly && (!e.sortAt || e.sortAt === e.at || e.sortAt.includes('T23:59:59')),
            };
        })
    );

    return {
        events,
        documents: Array.from(added, ([id, label]) => ({ id, label })),
    };
};

/**
 * What the connector wrote as a note, in the words of the screen. "Customer X: updating Exact account Y, changed: A,
 * B" is the connector sending changed customer details to Exact.
 */
export const humanize = (title: string, detail?: string | null): { title: string; detail?: string | null } => {
    const update = /^Customer .+?: updating Exact account [0-9a-f-]+(?:, changed: (.+?))?(?: \((\d+)×\))?$/i.exec(title);
    if (update) {
        const changed = update[1]?.replace(/\.\.\.$|…$/, '').trim();
        return {
            title: `Klantgegevens naar Exact${update[2] ? ` (${update[2]}×)` : ''}`,
            detail: changed ? `gewijzigd: ${changed}` : detail,
        };
    }
    return { title, detail };
};

/**
 * The connector's own "in Exact" moment and what Exact says it booked are one and the same step: keep the
 * connector's moment (it knows the time), named as Exact names it. Matched on the entry number both mention.
 */
const mergeSame = (events: TimelineEvent[], extra: TimelineEvent[]) => {
    const numbers = (e: TimelineEvent): string[] => `${e.title} ${e.detail ?? ''}`.match(/\d{5,}/g) ?? [];
    const merged = events.map((e) => ({ ...e }));
    const rest = extra.filter((x) => {
        if (x.lane !== 'ex' || x.state !== 'ok') return true;
        const wanted = numbers(x);
        const same = merged.find((e) => e.documentId === x.documentId && e.lane === 'ex' && e.state === 'ok' && numbers(e).some((n) => wanted.includes(n)));
        if (!same) return true;
        same.title = x.title;
        same.detail = x.detail ?? same.detail;
        return false;
    });
    return [...merged, ...rest];
};

// The kind of a document from the first word of its label, as the server names them (CockpitLanes.Noun, RoleName).
const RANKS: [RegExp, number][] = [
    [/^klant\b/i, 0],
    [/^(abonnement|artikel)\b/i, 1],
    [/^(factuur|verkoopfactuur|verkoopboeking)\b/i, 2],
    [/^(creditnota|creditfactuur|creditboeking)\b/i, 3],
    [/^(betaling|bankboeking)\b/i, 4],
    [/^terugbetaling\b/i, 5],
    [/^stornering\b/i, 6],
];

/** Where a document goes in the timeline: customer, invoice, credit note, payment, refund, chargeback, the rest. */
export const documentRank = (label: string) => RANKS.find(([pattern]) => pattern.test(label.trim()))?.[1] ?? 7;

export interface FlowContext {
    /** Documents that are in Exact now: their earlier failures are history, not alarms. */
    settled?: Set<string>;
    /** Extra words per failure title, such as the articles of the invoice when Exact did not find one. */
    explain?: (documentId: string, title: string) => string | undefined;
}

/** The timeline of /Cockpit as flow items: a phase per document as it first shows up, a NU line after the last event. */
export const timelineFlow = (
    timeline: Timeline,
    extra: SettlementExtra = { events: [], documents: [] },
    context: FlowContext = {}
): { items: FlowItem[]; documents: FlowDocument[] } => {
    const all = [...timeline.documents.map((d) => ({ id: d.id, label: d.label })), ...extra.documents];
    const byTime = mergeSame(timeline.events, extra.events).sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''));
    // Only the documents something happened to, in the order they first show up.
    const first = new Map<string, number>();
    byTime.forEach((e, i) => !first.has(e.documentId) && first.set(e.documentId, i));
    // Customer first, then the invoice, its credit notes and its payments, the way the money goes; in time within a kind.
    const order = (d: { id: string; label: string }) => documentRank(d.label);
    const documents = all
        .filter((d) => first.has(d.id))
        .sort((a, b) => order(a) - order(b) || first.get(a.id)! - first.get(b.id)!)
        .map((d, i) => ({
            id: d.id,
            label: d.label,
            color: DOC_COLORS[i % DOC_COLORS.length],
        }));
    // Per document, and in time within each: a heading holds only its own cards, and a line never has to run past
    // another document's.
    const position = new Map(documents.map((d, i) => [d.id, i]));
    const sorted = byTime
        .map((e, i) => ({ e, i }))
        .sort((a, b) => (position.get(a.e.documentId) ?? 99) - (position.get(b.e.documentId) ?? 99) || a.i - b.i)
        .map(({ e }) => e);

    // A failure before the last time its document reached Exact got through after all.
    const reached = new Map<string, number>();
    sorted.forEach((e, i) => {
        if (e.lane === 'ex' && e.state === 'ok') reached.set(e.documentId, i);
    });
    const healed = (e: TimelineEvent, i: number) => e.state === 'bad' && (i < (reached.get(e.documentId) ?? -1) || !!context.settled?.has(e.documentId));

    const seen = new Set<string>();
    const items: FlowItem[] = [];

    sorted.forEach((e, i) => {
        if (!seen.has(e.documentId)) {
            seen.add(e.documentId);
            items.push({
                phase: documents.find((d) => d.id === e.documentId)?.label ?? e.documentId,
            });
        }
        const words = humanize(e.title, e.detail);
        const more = e.state === 'bad' ? context.explain?.(e.documentId, e.title) : undefined;
        const look: FlowLook = healed(e, i) ? 'healed' : e.state;
        items.push({
            at: e.at,
            documentId: e.documentId,
            lane: e.lane,
            app: e.app,
            look,
            title: look === 'healed' ? `${words.title} · later gelukt` : words.title,
            // Healed: the connector's "later alsnog gelukt" is in the title already; what failed stays as detail.
            detail: look === 'healed' ? more ?? null : [more, words.detail].filter(Boolean).join(' · ') || null,
            tone: e.state === 'bad' && e.lane === 'cn' && !!e.app && look !== 'healed' ? 'bad' : undefined,
            direct: e.direct,
            dayOnly: e.dayOnly,
        });
    });
    items.push({ now: when(new Date()) });

    return { items, documents };
};
